import { FormControl, type TextFieldProps } from '@mui/material'
import { getIn, useFormikContext } from 'formik'
import { type Props as SelectProps, type SingleValue } from 'react-select'

import { getErrorLabelForPath, pathHasError } from '~/utils/form-utils'

// Project
import Autocomplete from './Autocomplete'
import StyledFormLabel from './StyledFormLabel'

type FormikValues = Record<string, unknown>

export interface AutocompleteOption<V = string | number> {
  label: string
  value: V
}

// react-select props forwarded as-is to Autocomplete through the spread.
// They are spread AFTER the internal ones, so callers can override them
// (e.g. Economics.tsx and FamilyMembers.jsx pass their own onChange).
type ForwardedSelectProps<V> = Partial<
  Omit<SelectProps<AutocompleteOption<V>, false>, 'name' | 'options' | 'value'>
>

// T: shape of each raw option. The option value is typed from T's own
// property types, so callers' onChange receives the real type of their data.
type OptionValue<T> = T[keyof T]

type AutocompleteWithFormikProps<T extends object> = ForwardedSelectProps<
  OptionValue<T>
> & {
  name: string
  label?: string
  required?: boolean
  textFieldProps?: TextFieldProps
  maxSelectMenuHeight?: number
  // "Raw" mode: options and current value are derived from rawOptions + keys.
  rawOptions?: T[]
  valueKey?: keyof T
  labelKey?: keyof T
  // "Direct" mode: options and value are passed already shaped.
  options?: Array<AutocompleteOption<OptionValue<T>>>
  value?: AutocompleteOption<OptionValue<T>> | null
}

// The default for T applies in "direct" mode (no rawOptions passed).
function AutocompleteWithFormik<T extends object = AutocompleteOption>({
  name,
  label,
  required,
  textFieldProps,
  rawOptions,
  valueKey,
  labelKey,
  value,
  options,
  maxSelectMenuHeight,
  ...props
}: AutocompleteWithFormikProps<T>) {
  const { t } = useTranslation()
  const formik = useFormikContext<FormikValues>()

  const error = pathHasError(name, formik.touched, formik.errors)
  const helperText = getErrorLabelForPath(
    name,
    formik.touched,
    formik.errors,
    t,
  )

  let effectiveOptions = options
  let effectiveValue = value

  // When rawOptions + valueKey + labelKey are all present, "raw" mode takes
  // precedence: it overrides `options` and `value` even if the parent also
  // passed them (e.g. UserOrgsSelector passes `value` together with rawOptions).
  if (rawOptions && valueKey && labelKey) {
    effectiveOptions = rawOptions.map(o => ({
      label: o[labelKey] as string,
      value: o[valueKey],
    }))

    // getIn instead of formik.values[name]: name can be a nested path
    // (e.g. `members[0].gender`). It uses the same path parser as setFieldValue.
    const valueFromFormik: unknown = getIn(formik.values, name)
    const selectedOption = rawOptions.find(o => o[valueKey] === valueFromFormik)
    const effectiveLabel = selectedOption
      ? (selectedOption[labelKey] as string)
      : ''

    effectiveValue = {
      // Formik holds whatever onChange wrote here: option.value or ''
      value: valueFromFormik as OptionValue<T>,
      label: valueFromFormik ? effectiveLabel : '',
    }
  }

  const onBlur = () => {
    setTimeout(() => {
      void formik.setFieldTouched(name)
    }, 250)
  }

  const onChange = (
    option: SingleValue<AutocompleteOption<OptionValue<T>>>,
  ) => {
    void formik.setFieldValue(name, option ? option.value : '')
  }

  const autocompleteProps = {
    name,
    options: effectiveOptions,
    value: effectiveValue,
    onBlur,
    onChange,
    maxSelectMenuHeight,
    fullWidth: true,
    // Intentionally last: parent props override the internal handlers.
    ...props,
  }

  const fieldProps: TextFieldProps = {
    required,
    label: '',
    error,
    helperText,
    ...textFieldProps,
  }

  return (
    <FormControl sx={{ width: '100%' }}>
      <StyledFormLabel component="legend" required={required} error={error}>
        {label}
      </StyledFormLabel>

      {/* @ts-expect-error Autocomplete.jsx is untyped legacy JS: tsc infers all its destructured props as required */}
      <Autocomplete {...autocompleteProps} textFieldProps={fieldProps} />
    </FormControl>
  )
}

export default AutocompleteWithFormik
